import { CheckIcon, PencilSimpleIcon } from '@phosphor-icons/react';
import {
  type ReactNode,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { ICON_BUTTON } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

export interface InlineFieldEditHandle {
  startEditing: () => void;
  /** Commits the current draft and exits edit mode. Used by parent toolbars
   *  that own the toggle button (e.g. About-section pencil/check button). */
  stopEditing: () => void;
  /** Whether the field is currently in edit mode. Lets the parent reflect
   *  edit-state in its own UI (e.g. swap pencil → check icon). */
  isEditing: () => boolean;
}

interface InlineFieldEditProps {
  value: string;
  placeholder?: string;
  multiline?: boolean;
  maxLength?: number;
  ariaLabel: string;
  onSave: (next: string) => void | Promise<void>;
  /**
   * Notifies the parent whenever the field's internal edit state flips.
   * Use it to keep an external trigger icon (e.g. the section-header pencil
   * → check button) in sync when the user enters edit mode by clicking the
   * displayed text directly instead of the section pencil.
   */
  onEditingChange?: (editing: boolean) => void;
  renderDisplay?: (value: string) => ReactNode;
  className?: string;
  displayClassName?: string;
  editClassName?: string;
  rows?: number;
  /**
   * Hide the built-in pencil/save button. Use when a parent section header
   * already renders an edit trigger next to the title; the parent calls
   * `ref.startEditing()` to enter edit mode. Prevents the "two pencils per
   * field" visual bug where a section has both an h3-aligned pencil and an
   * inline pencil.
   */
  hideAction?: boolean;
}

export const InlineFieldEdit = forwardRef<InlineFieldEditHandle, InlineFieldEditProps>(
  function InlineFieldEdit(
    {
      value,
      placeholder,
      multiline = false,
      maxLength,
      ariaLabel,
      onSave,
      onEditingChange,
      renderDisplay,
      className,
      displayClassName,
      editClassName,
      rows = 3,
      hideAction = false,
    },
    handleRef,
  ) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value);
    const [saving, setSaving] = useState(false);
    const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const committingRef = useRef(false);

    useImperativeHandle(handleRef, () => ({
      startEditing: () => setEditing(true),
      stopEditing: () => {
        if (editing) void commit();
      },
      isEditing: () => editing,
    }));

    // Keep external trigger UI (e.g. section-header pencil/check) in sync.
    useEffect(() => {
      onEditingChange?.(editing);
    }, [editing, onEditingChange]);

    useEffect(() => {
      if (!editing) setDraft(value);
    }, [value, editing]);

    useEffect(() => {
      if (editing && inputRef.current) {
        inputRef.current.focus();
        const len = inputRef.current.value.length;
        inputRef.current.setSelectionRange?.(len, len);
      }
    }, [editing]);

    const commit = async () => {
      if (committingRef.current) return;
      committingRef.current = true;
      const trimmed = draft.trim();
      if (trimmed === value.trim()) {
        setEditing(false);
        committingRef.current = false;
        return;
      }
      setSaving(true);
      try {
        await onSave(trimmed);
        setEditing(false);
      } finally {
        setSaving(false);
        committingRef.current = false;
      }
    };

    const cancel = () => {
      setDraft(value);
      setEditing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        cancel();
        return;
      }
      if (e.key === 'Enter') {
        if (multiline && !(e.metaKey || e.ctrlKey)) return;
        e.preventDefault();
        void commit();
      }
    };

    // Blur handler — only commit if focus leaves the whole container.
    // This prevents the race where clicking the save/pencil button fires onBlur
    // → commit → setEditing(false) → then the button's onClick re-enters edit mode.
    const handleBlur = (e: React.FocusEvent) => {
      const nextFocus = e.relatedTarget as Node | null;
      if (containerRef.current && nextFocus && containerRef.current.contains(nextFocus)) {
        return;
      }
      void commit();
    };

    const display = renderDisplay ? renderDisplay(value) : value || placeholder;

    return (
      <div
        ref={containerRef}
        className={cn('group/edit flex items-start justify-between gap-2', className)}
      >
        <div className="min-w-0 flex-1">
          {editing ? (
            multiline ? (
              <Textarea
                ref={inputRef as React.RefObject<HTMLTextAreaElement>}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                placeholder={placeholder}
                maxLength={maxLength}
                rows={rows}
                aria-label={ariaLabel}
                data-escape-revert="true"
                className={cn(
                  'resize-none border-border/60 text-caption leading-relaxed',
                  editClassName,
                )}
              />
            ) : (
              <Input
                ref={inputRef as React.RefObject<HTMLInputElement>}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                placeholder={placeholder}
                maxLength={maxLength}
                aria-label={ariaLabel}
                data-escape-revert="true"
                className={cn('h-8 text-caption', editClassName)}
              />
            )
          ) : (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className={cn(
                'group/display -mx-1 -my-0.5 block w-full rounded-sm px-1 py-0.5 text-left',
                'text-caption leading-relaxed transition-colors',
                'hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring/30',
                value ? 'text-muted-foreground/80' : 'text-muted-foreground/50',
                displayClassName,
              )}
              aria-label={`Edit ${ariaLabel}`}
            >
              {display}
            </button>
          )}
        </div>
        {!hideAction && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onMouseDown={(e) => {
                  // Prevent the input's blur from firing before this click.
                  if (editing) e.preventDefault();
                }}
                onClick={() => {
                  if (editing) void commit();
                  else setEditing(true);
                }}
                disabled={saving}
                className={cn(ICON_BUTTON, 'shrink-0 disabled:opacity-50')}
                aria-label={editing ? `Save ${ariaLabel}` : `Edit ${ariaLabel}`}
              >
                {saving ? (
                  <Spinner size="sm" />
                ) : editing ? (
                  <CheckIcon className="size-3.5" />
                ) : (
                  <PencilSimpleIcon className="size-3.5" />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent>{saving ? 'Saving…' : editing ? 'Save' : 'Edit'}</TooltipContent>
          </Tooltip>
        )}
        {hideAction && saving && (
          <span className={cn(ICON_BUTTON, 'shrink-0 opacity-60')} aria-hidden>
            <Spinner size="sm" />
          </span>
        )}
      </div>
    );
  },
);
